import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import { getPostBySlug, getRelatedPosts, categoryColors } from "@/data/blogPosts";
import { BlogPostCard } from "@/components/blog";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedPosts(slug, 2) : [];

  if (!post) {
    return (
      <div className="min-h-screen w-full flex flex-col bg-background">
        <Navbar />
        <main className="flex-grow flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-2xl bg-sidebar flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">404</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Article Not Found
            </h1>
            <p className="text-muted-foreground mb-8">
              The article you're looking for doesn't exist or may have been moved.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const colors = categoryColors[post.category];
  const excerpt = post.content.find(section => section.type === 'paragraph')?.text.substring(0, 160) + '...';

  // Set page title and meta description
  useEffect(() => {
    document.title = `${post.title} | Tag-Inn`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', excerpt || '');
    }
  }, [post.title, excerpt]);

  return (
    <div className="min-h-screen w-full flex flex-col bg-background">
      <Navbar />

      <main className="flex-grow">
        {/* Article header */}
        <header className="relative overflow-hidden bg-gradient-to-b from-primary/10 to-background dark:from-primary/20 dark:to-background">
          {/* Background decorations */}
          <div className="absolute inset-0">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-20">
            {/* Back button */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8 group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>

            {/* Category */}
            <div className="mb-6">
              <span className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase ${colors.bg} ${colors.text} border ${colors.border}`}>
                {post.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-tight mb-8">
              {post.title}
            </h1>

            {/* Meta information */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {post.author.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{post.author}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </time>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readingTime} min read</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </header>

        {/* Article content */}
        <article className="max-w-3xl mx-auto px-6 lg:px-8 py-12 md:py-16">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            {post.content.map((section, index) => (
              <div key={index} className="mb-8 last:mb-0">
                {section.type === 'paragraph' && (
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    {section.text}
                  </p>
                )}
                {section.type === 'heading' && (
                  <h2 className="text-2xl font-bold text-foreground mt-12 mb-4 first:mt-0">
                    {section.text}
                  </h2>
                )}
                {section.type === 'list' && (
                  <ul className="space-y-3 my-6">
                    {section.items?.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground mr-2">Tags:</span>
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-xs font-medium text-muted-foreground bg-sidebar rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Back to blog */}
          <div className="mt-12 pt-8 border-t border-border">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
          </div>
        </article>

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <section className="border-t border-border bg-sidebar/30">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20">
              <div className="flex items-center justify-between mb-10">
                <h2 className="text-2xl font-bold text-foreground">
                  Related Articles
                </h2>
                <Link
                  to="/blog"
                  className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
                >
                  View all
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {relatedPosts.map((relatedPost) => (
                  <BlogPostCard key={relatedPost.id} post={relatedPost} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
